// Extrait du Dashboard Finance — Visites par mois (BarChart stacked
// payées/non payées) + détail par visiteur sur 6 mois.
// Données : /api/metier/stats/visites-amo
import { Eye, Loader2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { useFinanceFetch } from "./useFinanceFetch";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-xl border"
      style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
    >
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((e, i) => (
        <p key={i} style={{ color: e.color }}>
          {e.name}: <span className="font-bold">{e.value}</span>
        </p>
      ))}
    </div>
  );
};

const MONTHS_SHORT = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const MONTHS_LONG = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export default function FinanceVisitesAmoWidget() {
  const { data, loading, error } = useFinanceFetch("/api/metier/stats/visites-amo");

  if (loading) {
    return (
      <div
        className="rounded-xl p-5 h-full flex items-center justify-center"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
      >
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="rounded-xl p-5"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }}
      >
        Erreur Visites : {error}
      </div>
    );
  }

  const evolution = data?.evolution_mensuelle || [];
  const detailMoisVisiteur = data?.detail_mois_visiteur || [];

  if (evolution.length === 0) {
    return (
      <div
        className="rounded-xl p-5 h-full text-center"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }}
        data-testid="finance-visites-amo-widget"
      >
        Aucune visite à afficher.
      </div>
    );
  }

  const chartData = evolution.map((m) => {
    const [y, mo] = m.mois.split("-");
    return {
      ...m,
      label: `${MONTHS_SHORT[parseInt(mo) - 1]} ${y.slice(2)}`,
    };
  });

  return (
    <div
      className="rounded-xl p-5 h-full"
      style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
      data-testid="finance-visites-amo-widget"
    >
      <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
        <Eye className="w-4 h-4" style={{ color: "var(--oh-stat-amber-text)" }} />
        Visites par mois
        <Badge
          className="ml-2 text-xs"
          style={{
            background: "rgba(245,158,11,0.15)",
            color: "var(--oh-stat-amber-text)",
            border: "1px solid rgba(245,158,11,0.3)",
          }}
        >
          Objectif suivi
        </Badge>
      </h3>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--oh-border)" />
          <XAxis dataKey="label" tick={{ fill: "var(--oh-text-muted)", fontSize: 11 }} />
          <YAxis tick={{ fill: "var(--oh-text-muted)", fontSize: 11 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top" align="right" iconType="square" iconSize={10}
            wrapperStyle={{ fontSize: "12px", color: "var(--oh-text-muted)", paddingBottom: "8px" }}
          />
          <Bar dataKey="payees" name="Payées" stackId="a" fill="#10b981" />
          <Bar dataKey="non_payees" name="Non payées" stackId="a" fill="var(--oh-stat-red-text)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      {/* Détail par mois/visiteur — 6 derniers mois */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {evolution.slice(-6).reverse().map((m) => {
          const [y, mo] = m.mois.split("-");
          const label = `${MONTHS_LONG[parseInt(mo) - 1]} ${y}`;
          const detail = detailMoisVisiteur.filter((d) => d.mois === m.mois);
          return (
            <div
              key={m.mois}
              className="rounded-lg p-3"
              style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold" style={{ color: "var(--oh-text)" }}>{label}</span>
                <div className="flex items-center gap-2 text-xs">
                  <span style={{ color: "#10b981" }}>{m.payees} payées</span>
                  <span style={{ color: "var(--oh-stat-red-text)" }}>{m.non_payees} non</span>
                  <span className="font-bold" style={{ color: "var(--oh-text)" }}>{m.visites} total</span>
                </div>
              </div>
              {detail.length > 0 && (
                <div className="space-y-1">
                  {detail.map((d) => (
                    <div key={d.visiteur} className="flex items-center justify-between text-xs">
                      <span style={{ color: "var(--oh-text-secondary)" }}>{d.visiteur}</span>
                      <span>
                        <span style={{ color: "#10b981" }}>{d.payees}</span>
                        <span style={{ color: "var(--oh-text-muted)" }}> / </span>
                        <span style={{ color: "var(--oh-text)" }}>{d.visites}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
